import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { selectors as memberSelectors } from 'member-reducer'
import {
  Field,
  formValues,
  SubmissionError,
  propTypes as formPropTypes,
} from 'redux-form'
import { lpForm } from 'lp-form'
import {
  Checkbox,
  Input,
  MaskedInput,
  RadioGroup,
  SubmitButton,
} from 'lp-components'
import {
  AutoRenewalConsentField,
  AutoRenewalContent,
  AutomaticRenewalWarningModal,
  TermsAgreementField,
} from 'member-portal/components'
import { USER_RULES } from 'config'
import { DONATION_CONTRIBUTION_AMOUNTS } from 'types'
import { parseNumberFromString, useToggle } from 'utils'

const propTypes = {
  ...formPropTypes,
  donationAmounts: PropTypes.string,
  initialValues: PropTypes.object.isRequired,
  isTrialMembershipsActive: PropTypes.bool,
  promoCodeIsTrial: PropTypes.bool,
  showReferral: PropTypes.bool,
}

function TermsAndConditionsForm({
  autoRenewalAgreement = false,
  change,
  donationAmounts = null,
  handleSubmit,
  initialValues,
  isTrialMembershipsActive = false,
  promoCodeIsTrial = false,
  showReferral = false, 
  submitting = false,
}) {
  const [autoRenewalModalDisplayed, toggleAutoRenewalModalDisplay] =
    useToggle(false)
  const [showMemberName, setShowMemberName] = useState(false)
  const [showReferralField, setShowReferralField] = useState(
    !!initialValues?.referredByMemberNumber ||
    !!initialValues?.isReferredByMember,
  )

  const {
    fetchedReferrerMemberName,
    fetchedReferrerMemberNumber,
    referredByMemberNumber,
  } = initialValues ?? {}

  const handleMemberNumberChange = (e) => {
    const { value } = e.target
    setShowMemberName(
      fetchedReferrerMemberNumber && value === fetchedReferrerMemberNumber,
    )
  }

  useEffect(() => {
    if (!fetchedReferrerMemberNumber) return

    setShowMemberName(referredByMemberNumber === fetchedReferrerMemberNumber)
  }, [referredByMemberNumber, fetchedReferrerMemberNumber])

  return (
    <>
      <AutomaticRenewalWarningModal
        isOpen={autoRenewalModalDisplayed}
        toggleModalOpen={toggleAutoRenewalModalDisplay}
      />
      {promoCodeIsTrial && isTrialMembershipsActive && <div></div>}

      <form noValidate onSubmit={handleSubmit}>
        {showReferral && (
          <>
            <hr className="divider" />
            <Field
              component={Checkbox}
              label="Referred by a Sea Tow Member?"
              name="isReferredByMember"
              onChange={(isReferredByMember) => {
                setShowReferralField(isReferredByMember)
                if (!isReferredByMember) {
                  change('referredByMemberNumber', '')
                }
              }}
            />
          </>
        )}

        {showReferralField && (
          <div className="referred-by-wrapper">
            <div className="col-6">
              <Field
                className="referring-member-number"
                component={Input}
                label={false}
                name="referredByMemberNumber"
                placeholder="Sea Tow member number"
                aria-label="Sea Tow member number who referred you"
                onChange={handleMemberNumberChange}
              />
              {showMemberName && (
                <span className="referring-member-name">
                  Name: {fetchedReferrerMemberName}
                </span>
              )}
            </div>
          </div>
        )}

        <hr className="divider" />

        <div
          aria-labelledby="category-header"
          role="radiogroup"
          className="group-block small-spacing inline-radio-list"
        >
          <label htmlFor="donationAmounts">
            Donate to the Sea Tow Foundation
          </label>
          <p>Be a part of making our waterways safer for all boaters.</p>
          <Field
            component={RadioGroup}
            id="donationAmounts"
            label={false}
            name="donationAmounts"
            onChange={() => change('otherDollarDonationAmount', null)}
            options={Object.values(DONATION_CONTRIBUTION_AMOUNTS)}
          />
          {donationAmounts === DONATION_CONTRIBUTION_AMOUNTS.OTHER && (
            <Field
              component={MaskedInput}
              label={false}
              name="otherDollarDonationAmount"
              normalize={parseNumberFromString}
              maskOptions={{
                noImmediatePrefix: true,
                numeral: true,
                numeralPositiveOnly: true,
                prefix: '$',
              }}
            />
          )}
        </div>

        <>
          <hr className="divider" />
          <Field
            component={Checkbox}
            label="Automatic Renewal"
            name="autoRenewalAgreement"
            onChange={(autoRenewalAgreement) => {
              if (!autoRenewalAgreement) {
                toggleAutoRenewalModalDisplay()
                change('autoRenewalConsent', false)
              }
            }}
          />
          <AutoRenewalContent />
        </>

        <div className="consent-checkboxes">
          <TermsAgreementField />
          {autoRenewalAgreement && <AutoRenewalConsentField />}
        </div>

        <div className="button-group">
          <SubmitButton {...{ submitting }}>Save & Continue</SubmitButton>
        </div>
      </form>
    </>
  )
}

const beforeSubmit = (formValues, { discountCodeDetails, totalPayment }) => {
  const { autoRenewalAgreement, autoRenewalConsent, termsAgreement } = formValues
  const { usage_rules } = discountCodeDetails || {}
  const errors = {}

  if (!termsAgreement) {
    errors.termsAgreement = 'You must agree to the terms and conditions to continue.'
  }

  if (autoRenewalAgreement && !autoRenewalConsent) {
    errors.autoRenewalConsent = 'You must agree to the automatic renewal terms to continue.'
  }

  if (!autoRenewalAgreement && usage_rules === USER_RULES.ARB_REQUIRED) {
    errors.autoRenewalAgreement = 'Automatic Renewal selection required to use this Activation Code.'
  }

  if (Object.keys(errors).length > 0) {
    throw new SubmissionError(errors)
  }

  if (!autoRenewalAgreement && totalPayment === 0) {
    return { ...formValues }
  }

  return formValues
}

TermsAndConditionsForm.propTypes = propTypes

function mapStateToProps(state) {
  return {
    discountCodeDetails: memberSelectors.discountCodeDetails(state),
    isTrialMembershipsActive: memberSelectors.isTrialMembershipsActive(state),
    promoCodeIsTrial: memberSelectors.promoCodeIsTrial(state),
  }
}

export default compose(
  lpForm({
    beforeSubmit,
    name: 'TermsAndConditionsForm',
  }),
  formValues(
    'autoRenewalAgreement',
    'autoRenewalConsent',
    'donationAmounts',
    'otherDollarDonationAmount',
    'isReferredByMember',
  ),
  connect(mapStateToProps, null),
)(TermsAndConditionsForm)
